/*
  SectionComponent

  Displays all nested elements
*/

import {
  Component,
  Input,
  OnChanges,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { merge } from 'lodash-es';
import { NgForm } from '@angular/forms';
import { filter, Observable } from 'rxjs';

import {
  abaMask,
  currencyMask,
  einMask,
  giinMask,
  itinMask,
  ssnMask,
  vehiclePercentMask
} from '@helpers/masks';
import { ClassHelper } from '@app/shared/helpers/class.helper';
import { ConfirmationModalComponent, ConfirmationModalData } from '@component/confirmation-modal';
import { FilestackClient } from '@app/shared/services/vendor/filestack.client';
import { FormSection } from '@state/form';
import { FormWizardQuery, FormWizardStore } from '@state/form-wizard';
import { FroalaHelper } from '@app/shared/services/vendor';
import { inputEmailPattern } from '@helpers/validation-patterns';
import { noIndent } from '@helpers/string';
import { SignatureWizardDialogComponent } from '@app/shared/components/dialogs/signature-wizard/signature-wizard-dialog.component';
import { TaskQuery } from '@state/task';
import { TypedSimpleChanges } from '@state/base';

import { RenderedFormService } from '../rendered-form.service';

// TODO: This file uses various any / undefined class definitions that we can probably
// get definitive definitions for from the server, should pair and add eventually

// TODO: We're mutating the sections that are input into the component here,
// this should absolutely be done through an akita store at whatever level the data
// is actually retrieved from

// Provides typing required for the recieved context of an element template
interface TemplateContext {
  element: any; // TODO: we should define what "element" actually is or could be for safety
  showpassword?: boolean;
  touched?: boolean;
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'fl-form-section',
  styleUrls: ['./section.component.scss'],
  templateUrl: './section.component.html'
})
export class SectionComponent implements OnInit, OnChanges {
  @Input() forceReadonly = false;
  @Input() form: any;
  @Input() section: any;
  @Input() sectionForms: Array<NgForm>;

  @ViewChild('sectionForm', { static: true }) sectionForm: NgForm;

  @ViewChild('defaultTemplate', { static: true }) defaultTemplate: TemplateRef<TemplateContext>;

  @ViewChild('fixedTextTemplate', { static: true }) fixedTextTemplate: TemplateRef<TemplateContext>;
  @ViewChild('textInputTemplate', { static: true }) textInputTemplate: TemplateRef<TemplateContext>;
  @ViewChild('hiddenTemplate', { static: true }) hiddenTemplate: TemplateRef<TemplateContext>;
  @ViewChild('passwordTemplate', { static: true }) passwordTemplate: TemplateRef<TemplateContext>;
  @ViewChild('textAreaTemplate', { static: true }) textAreaTemplate: TemplateRef<TemplateContext>;
  @ViewChild('slideToggleTemplate', { static: true }) slideToggleTemplate: TemplateRef<TemplateContext>;
  @ViewChild('checkboxTemplate', { static: true }) checkboxTemplate: TemplateRef<TemplateContext>;
  @ViewChild('multiSelectTemplate', { static: true }) multiSelectTemplate: TemplateRef<TemplateContext>;
  @ViewChild('radioTemplate', { static: true }) radioTemplate: TemplateRef<TemplateContext>;
  @ViewChild('datePickerTemplate', { static: true }) datePickerTemplate: TemplateRef<TemplateContext>;
  @ViewChild('customTemplate', { static: true }) customTemplate: TemplateRef<TemplateContext>;
  @ViewChild('documentTemplate', { static: true }) documentTemplate: TemplateRef<TemplateContext>;
  @ViewChild('signatureTemplate', { static: true }) signatureTemplate: TemplateRef<TemplateContext>;
  @ViewChild('contentTemplate', { static: true }) contentTemplate: TemplateRef<TemplateContext>;

  taskLoading$: Observable<boolean>;
  isSubmitting$ = this.formWizardQuery.isSubmitting$;
  showWarningOnUpdate$ = this.formWizardQuery.showWarningOnUpdate$;

  appliedSignature = false;
  orderedElements: any[] = [];
  showPassword = false;

  readonly MASKS = {
    aba: abaMask,
    currency: currencyMask,
    ein: einMask,
    giin: giinMask,
    itin: itinMask,
    percent: vehiclePercentMask,
    phone: { mask: ['(', /\d/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/] },
    ssn: ssnMask
  };

  readonly PATTERNS = {
    email: inputEmailPattern
  };

  constructor(
    private filestackClient: FilestackClient,
    private formWizardQuery: FormWizardQuery,
    private formWizardStore: FormWizardStore,
    private taskQuery: TaskQuery,
    public dialog: MatDialog,
    public froalaHelper: FroalaHelper,
    public renderedFormService: RenderedFormService
  ) {}

  ngOnInit() {
    this.sectionForms.push(this.sectionForm);
    this.taskLoading$ = this.taskQuery.selectLoading();
  }

  ngOnChanges(changes: TypedSimpleChanges<SectionComponent>) {
    // This updates form values on recieving new section data
    // so that the view is always up to date
    let { section } = changes;

    if (section) {
      void this.updateFormValues({ skipLiquid: true });
      this.setOrderedElements(section.currentValue as FormSection);
      void this.parseAndRenderLiquid();
    }
  }

  // TODO: Rather than setting elements, and then later parsing with liquid
  // we should determine an appropriate repository where we can create a
  // query to get the parsed elements that have already parsed
  setOrderedElements(section: FormSection): void {
    let groups = {};
    let elements: any[] = [];

    // 1st Pass, group elements by groupId
    for (let element of section.elements) {
      if (element.configuration.groupId) {
        if (groups[element.configuration.groupId] === undefined) {
          groups[element.configuration.groupId] = {
            element_object_type: 'group',
            elements: [],
            position: element.position
          };
        }

        groups[element.configuration.groupId].elements.push(element);
      } else {
        elements.push(element);
      }
    }

    this.orderedElements = elements.concat(Object.values(groups));
    this.orderedElements.sort((a, b) => a.position - b.position);
  }

  get valid(): boolean {
    if (this.sectionForm.valid) {
      this.formWizardStore.update({ showWarningOnUpdate: false });
    }
    if (!this.sectionForm.form.valid) {
      let controls = Object.values(this.sectionForm.form.controls);
      if (controls.every((control) => control.status === 'DISABLED')) return true;
    }
    if (!this.renderedFormService.dynamicModels['APPLIED_SIGNATURE']) this.appliedSignature = false;
    return this.sectionForm.form.valid;
  }

  resetElements(): void {
    delete this.orderedElements;
  }

  // Need to cast values from the server to boolean in order for mat-checkbox to check/uncheck.
  booleanCastedValue(value: string | boolean): boolean {
    return String(value).toLowerCase() === 'true';
  }

  customOptionSelected(renderedFormVal: any, optionVal: any): boolean {
    if (renderedFormVal !== null && renderedFormVal !== undefined) {
      renderedFormVal = renderedFormVal.toString();
    }
    return renderedFormVal === optionVal.toString();
  }

  elementObjectTypeClass(element: any): string {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return `element-object-type-${element.element_object_type.replace(/\//, '-')}`;
  }

  isFirstObjectInArray(list: any[], object: any): boolean {
    return list[0].id === object.id;
  }

  groupClass(element: { configuration: { groupSpan: any } }): string {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    return element.configuration.groupSpan ? `flex-cell-${element.configuration.groupSpan}` : 'flex-cell-1';
  }

  onCheckboxChange(event: MatCheckboxChange, key: any): void {
    this.renderedFormService.dynamicModels[key] = String(event.checked).toLowerCase();
    void this.updateFormValues();
  }

  onDatePickerClick(
    datePicker: MatDatepicker<any>,
    element: { element_object: { read_only: boolean } }
  ): void {
    if (!element.element_object.read_only) {
      datePicker.open();
    }
  }

  onUploadWidgetClick(key: string, accept: string): void {
    let parsedAcceptParam = this.parseAcceptParam(accept);
    let filePickerOptions = {};

    if (parsedAcceptParam) {
      filePickerOptions = {
        accept: parsedAcceptParam
      };
    }

    this.filestackClient.showDocumentPicker({
      onSuccess: (data) => {
        if (data.length > 0) {
          let { id, title, signed_url } = data[0];
          this.renderedFormService.dynamicModels[key] = {
            id,
            signed_url,
            title,
          };
        }
      }
    }, filePickerOptions);
  }

  onRemoveDocumentUpload(key: string): void {
    let { title } = this.renderedFormService.dynamicModels[key];

    let data: ConfirmationModalData = {
      message: noIndent(
        `Are you sure you want to archive <strong>${title}</strong>?\n
        Archiving this document will remove it from any investments associated with this investment profile.`
      ),
      positiveButtonLabel: 'Archive',
      title: 'Archive Uploaded Document'
    };

    this.dialog
      .open(ConfirmationModalComponent, { data })
      .afterClosed()
      .pipe(
        filter((confirmed: boolean) => confirmed)
      )
      .subscribe({
        next: () => {
          this.renderedFormService.dynamicModels[key] = null;
        }
      });
  }

  openSignatureDialog(custom_variable_key: string): void {
    const dialogRef = this.dialog.open(SignatureWizardDialogComponent, {
      data: { signatureText: this.renderedFormService.dynamicModels[custom_variable_key] },
      width: '500px'
    });

    dialogRef.afterClosed().subscribe({
      next: (result) => {
        if (!result) {
          return;
        }
        this.renderedFormService.dynamicModels[custom_variable_key] = result;
        this.renderedFormService.dynamicModels['APPLIED_SIGNATURE'] = true;
        this.appliedSignature = true;
      }
    });
  }

  parseAcceptParam(accept: string): string[] | string {
    if (!accept) return;

    try {
      accept = JSON.parse(accept);
    } catch {}

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return accept;
  }

  async parseAndRenderLiquid(): Promise<void> {
    for (let [index, element] of this.orderedElements.entries()) {
      if (element.element_object_type !== 'content') continue;

      let liquidVariables = this.parsedVariables(this.renderedFormService.dynamicModels);
      let parsed_raw_html = await this.renderedFormService.liquidEngine.parseAndRender(
        element.element_object.raw_html,
        liquidVariables
      );

      let element_object = { ...element.element_object, parsed_raw_html };
      this.orderedElements[index] = { ...element, element_object };
    }
  }

  // Receives variables with custom values
  // { "USER.EMAIL": "xxx", ... }
  // and parse them to be used in the Liquid render method
  // { "USER": { "EMAIL": "xxx", ... }
  parsedVariables(variables: any[]): object {
    let liquidVariables = {};

    // eslint-disable-next-line @typescript-eslint/no-for-in-array
    for (const key in variables) {
      if (variables.hasOwnProperty(key)) {
        let variable_keys = key.split('.');
        let value = variables[key];
        let obj = variable_keys.reduceRight((all, item) => ({ [item]: all }), value);

        liquidVariables = merge(liquidVariables, obj);
      }
    }

    return liquidVariables;
  }

  setCustomValue(key: string, value: any): void {
    if (this.forceReadonly || this.renderedFormService.isSubmitting) return;
    this.renderedFormService.dynamicModels[key] = value;
    void this.updateFormValues();
  }

  async updateFormValues(options: { skipLiquid?: boolean } = {}): Promise<void> {
    let { skipLiquid } = options;
    let { visibleSections } = this.renderedFormService;
    this.formWizardStore.update({ visibleSections });

    if (!skipLiquid) await this.parseAndRenderLiquid();
  }

  visibleContent(form: any, element: HTMLElement): boolean {
    return (
      !form.rtd ||
      !this.isFirstObjectInArray(form.sections, this.section) ||
      !this.isFirstObjectInArray(this.section.elements, element)
    );
  }

  whichTemplate(element: any): TemplateRef<TemplateContext> {
    if (element == null) return;

    let templateClass = '';
    if (element.element_object_type === 'form/field') {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      templateClass = `${ClassHelper.camelize(element.element_object.widget_type)}Template`;
    } else if (element.element_object_type === 'content') {
      templateClass = 'contentTemplate';
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this[templateClass] || this.defaultTemplate;
  }
}
